import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/seo-agency-reviews-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/seo-agency-reviews-colorado")({
  head: () => ({
    meta: [
      { title: "How to Read SEO Agency Reviews (And Spot the Fake Ones) | 2026" },
      { name: "description", content: "SEO agency reviews can be gamed. How to read reviews the right way, spot fake patterns, and verify an agency before you sign anything." },
      { property: "og:title", content: "How to Read SEO Agency Reviews (And Spot the Fake Ones)" },
      { property: "og:description", content: "How to read SEO agency reviews the right way, spot fake patterns, and verify an agency before you sign." },
      { property: "og:url", content: `${SITE_URL}/learn/seo-agency-reviews-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "How to read SEO agency reviews",
        description: ARTICLE?.desc ?? "Spot fake review patterns and verify an SEO agency before signing.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/seo-agency-reviews-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Buyer's Guide"
      read="8 min read"
      title="How to read SEO agency reviews (and spot the fake ones)"
      intro="Every SEO agency has five stars somewhere. Reviews are the easiest thing to fake and the first thing buyers check — here's how to read them like a skeptic and verify an agency before you sign anything."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "SEO agency reviews" },
      ]}
      path="/learn/seo-agency-reviews-colorado"
      faqs={[
        { q: "Can SEO agency reviews be faked?", a: "Yes — reviews are one of the most gamed signals in marketing. Look for patterns: a burst of generic five-star reviews in one week, reviewer profiles with no other activity, or praise that never mentions specifics. Real reviews mention real details." },
        { q: "Where should I check an SEO agency's reviews?", a: "Start with their Google Business Profile — it's the hardest to fake at scale and shows how they respond to criticism. Then check how long they've been collecting reviews. A 27-year-old business with reviews spanning years beats a new profile with 50 reviews from last month." },
        { q: "What matters more than reviews when choosing an SEO agency?", a: "Proof you can verify: real client rankings you can search yourself, published pricing, month-to-month terms, and a willingness to show you exactly what they'll do in month one. Reviews support the decision; they shouldn't make it." },
      ]}
    >
      <h2>The patterns that give fake reviews away</h2>
      <ul>
        <li><strong>The burst.</strong> Forty reviews in two weeks after years of silence. Real reviews trickle in over years.</li>
        <li><strong>Generic praise.</strong> &ldquo;Great service, highly recommend!&rdquo; with no mention of what was actually done. Real customers talk about specifics — the rankings, the calls, the person they worked with.</li>
        <li><strong>Empty reviewer profiles.</strong> One review ever, no photo, no history. Real customers review other businesses too.</li>
        <li><strong>No responses from the owner.</strong> Agencies that don't reply to reviews — good or bad — usually aren't paying attention to clients either.</li>
      </ul>

      <h2>How to verify an agency beyond reviews</h2>
      <ul>
        <li><strong>Search their clients yourself.</strong> Ask for two or three clients and their target keywords, then Google them. Page one is proof; a testimonial is not.</li>
        <li><strong>Check their own rankings.</strong> An SEO agency that can't rank its own site in its own city is telling you something.</li>
        <li><strong>Look at longevity.</strong> We've been at this 27 years from Colorado Springs — through every Google update. Agencies that survive that long do it on results, because contracts alone don't keep clients that long.</li>
        <li><strong>Read the negative reviews.</strong> Every real business has a few. How the owner responds tells you more than the complaint does.</li>
      </ul>

      <h2>A note on our own reviews</h2>
      <p>
        Don't take our word for any of this — check us the same way. Our{" "}
        <Link to="/colorado-springs-office-woodmen-rd">Google Business Profile</Link> shows a
        5.0 rating from 149 reviews collected over years of real client work. Then search our
        clients' rankings yourself. That's the verification we'd want anyone to do.
      </p>

      <h2>What to do next</h2>
      <p>
        Want an honest look at your current agency's work — or your own online presence?
        Request a <Link to="/free-audit">free digital marketing audit</Link>. And if you're
        comparing agencies, our guide to the{" "}
        <Link to="/learn/best-seo-companies-colorado">best SEO companies in Colorado</Link> has
        the full buyer's checklist.
      </p>
    </ArticleLayout>
  );
}
